import * as z from "zod";
import { effortLevels } from "../aiProviders";
import { userFacingError } from "../authorization";
import { zql } from "../generatedDrizzleSchema";
import { assertPersonalWorkEnabled } from "../personalWorkGuard";
import { stripNullBytes } from "../stripNullBytes";
import {
  assertChatAttachmentsAllowed,
  attachmentSchema,
} from "./attachmentValidation";
import { mutator } from "./mutator";
import { insertUserInterruptEntry, nextRespondingAt } from "./utils";
import { clientGeneratableIdSchema } from "../id";

export const sendPersonalChatMessageSchema = z.object({
  chatId: z.string(),
  entryId: clientGeneratableIdSchema,
  text: z.string().transform(stripNullBytes),
  isDictated: z.boolean().optional(),
  planMode: z.boolean().optional(),
  effort: z.enum(effortLevels).optional(),
  chatModel: z.string().optional(),
  attachments: z.array(attachmentSchema).optional(),
  interruptEntryId: clientGeneratableIdSchema.optional(),
  clientSentAt: z.number().int().optional(),
  clientTimezone: z.string().optional(),
});

export const sendPersonalChatMessage = mutator({
  redactKeysOnSecret: ["text"],
  schema: sendPersonalChatMessageSchema,
  resolve: {},
  permissions: {
    none: "the chat is resolved through the caller’s own personalChats row; entries and attachments are written only into that chat",
  },
  fn: async ({ tx, ctx, args }) => {
    await assertPersonalWorkEnabled(tx, ctx.tenantId);
    const {
      chatId,
      entryId,
      text,
      isDictated,
      planMode,
      effort,
      attachments,
      interruptEntryId,
    } = args;
    if (!text.trim() && (!attachments || attachments.length === 0)) {
      throw userFacingError("Message must have text or attachments");
    }
    assertChatAttachmentsAllowed(attachments);

    const { userId } = ctx;
    const [pc] = await tx.run(
      zql.personalChats.where("chatId", chatId).where("userId", userId)
    );
    if (!pc) throw userFacingError("Personal chat not found");
    const [chat] = await tx.run(zql.chats.where("id", chatId));
    if (!chat) throw userFacingError("Chat not found");

    const createdAt = Date.now();

    const lastEntries = await tx.run(
      zql.chatEntries
        .where("chatId", chatId)
        .orderBy("createdAt", "desc")
        .limit(1)
    );
    const previousEntryId = lastEntries[0]?.id ?? null;

    if (interruptEntryId) {
      await insertUserInterruptEntry(tx, {
        id: interruptEntryId,
        chatId,
        createdAt,
        previousEntryId,
        tenantId: ctx.tenantId,
      });
    }

    await tx.mutate.chatEntries.insert({
      id: entryId,
      chatId,
      entryType: "UserMessage",
      createdAt: interruptEntryId ? createdAt + 1 : createdAt,
      previousEntryId: interruptEntryId ?? previousEntryId,
      tenantId: ctx.tenantId,
    });
    await tx.mutate.userMessages.insert({
      entryId,
      userId,
      text,
      isDictated: isDictated ?? null,
      planMode: planMode ?? null,
      effort: effort ?? null,
      tenantId: ctx.tenantId,
    });

    for (const att of attachments ?? []) {
      await tx.mutate.attachments.insert({
        id: att.id,
        entryId,
        fileName: att.fileName,
        mediaType: att.mediaType,
        sizeBytes: att.sizeBytes,
        tenantId: ctx.tenantId,
      });
    }

    const respondingAt = nextRespondingAt(chat.respondingAt);
    await tx.mutate.chats.update({
      id: chatId,
      lastChatEntryType: "UserMessage",
      respondingAt,
      awaitingAction: false,
    });
    await tx.mutate.personalChats.update({
      id: pc.id,
      lastActiveAt: createdAt,
    });
  },
});
